import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Share2, Download, Copy, Check, Send } from "lucide-react";
import { toast } from "sonner";
import { ORG_NAME, fmtMoney } from "../lib/donorProfile";
import { IMAGES } from "../data/content";

/**
 * ImpactCard — the square a donor shares after a gift. "Yellow poster":
 * solid brand yellow, the colour logo as a sticker, the student's photo as
 * a cut circle, the amount as the hero, the donor's name in their own hand,
 * the five palette dashes as the sign-off. The preview IS the exported
 * 1080×1080 PNG, so what donors see is exactly what they post.
 */
const C = {
  yellow: "#FDD006",
  ink: "#1A1714",
  olive: "#57503F",
  tangerine: "#FD6106",
  cobalt: "#0322F4",
  grass: "#0A9B06",
  blossom: "#FD98D9",
  scarlet: "#F92408",
  sand: "#F9F6F0",
};
const DASHES = [C.tangerine, C.cobalt, C.grass, C.blossom, C.scarlet];
const W = 1080;
const P = 84;

// Whole dollars read as "$50"; cents only when they exist.
const money = (n) => (Number.isInteger(Number(n)) ? `$${Number(n).toLocaleString("en-US")}` : fmtMoney(n));

const loadImage = (src) =>
  new Promise((resolve) => {
    if (!src) return resolve(null);
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });

const roundRect = (ctx, x, y, w, h, r) => {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
};

const wrap = (ctx, text, maxW) => {
  const words = text.split(" ");
  const lines = [];
  let cur = "";
  words.forEach((w) => {
    const t = cur ? `${cur} ${w}` : w;
    if (ctx.measureText(t).width > maxW && cur) { lines.push(cur); cur = w; } else cur = t;
  });
  if (cur) lines.push(cur);
  return lines;
};

// Square (1080×1080) for feeds; Story (1080×1920) keeps everything inside the
// band that Instagram/TikTok UI leaves uncovered (~260px top and bottom).
const FORMATS = {
  square: { h: 1080, top: P, bottom: 1080 - P, sticker: 186, photo: 268, head: 380, headSize: 100, headMax: 770, amount: 260 },
  story: { h: 1920, top: 300, bottom: 1620, sticker: 220, photo: 340, head: 760, headSize: 118, headMax: 912, amount: 300 },
};

const drawCard = async ({ format = "square", headline, amountText, support, signature, homeStretch, logo, photo }) => {
  if (typeof document !== "undefined" && document.fonts?.ready) {
    try { await document.fonts.ready; } catch (e) { /* noop */ }
  }
  const F = FORMATS[format] || FORMATS.square;
  const H = F.h;
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");

  ctx.fillStyle = C.yellow;
  ctx.fillRect(0, 0, W, H);

  // Sticker: the colour logo, slightly tilted, soft shadow
  if (logo) {
    const s = F.sticker;
    ctx.save();
    ctx.translate(P + s / 2, F.top + s / 2);
    ctx.rotate((-6 * Math.PI) / 180);
    ctx.shadowColor = "rgba(0,0,0,0.28)";
    ctx.shadowBlur = 34;
    ctx.shadowOffsetY = 16;
    ctx.fillStyle = "#FFFFFF";
    roundRect(ctx, -s / 2, -s / 2, s, s, 30);
    ctx.fill();
    ctx.shadowColor = "transparent";
    ctx.save();
    roundRect(ctx, -s / 2, -s / 2, s, s, 30);
    ctx.clip();
    ctx.drawImage(logo, -s / 2, -s / 2, s, s);
    ctx.restore();
    ctx.strokeStyle = "rgba(26,23,20,0.1)";
    ctx.lineWidth = 2;
    roundRect(ctx, -s / 2, -s / 2, s, s, 30);
    ctx.stroke();
    ctx.restore();
  }

  // Student photo: cut circle with an ink ring
  if (photo) {
    const d = F.photo;
    const cx = W - P - d / 2;
    const cy = F.top + d / 2;
    ctx.save();
    ctx.beginPath();
    ctx.arc(cx, cy, d / 2, 0, Math.PI * 2);
    ctx.clip();
    const scale = Math.max(d / photo.naturalWidth, d / photo.naturalHeight);
    const dw = photo.naturalWidth * scale;
    const dh = photo.naturalHeight * scale;
    // Faces sit high in portraits: bias the crop toward the top third, still covering the circle.
    const dy = Math.min(cy - d / 2, Math.max(cy + d / 2 - dh, cy - dh * 0.32));
    ctx.drawImage(photo, cx - dw / 2, dy, dw, dh);
    ctx.restore();
    ctx.strokeStyle = C.ink;
    ctx.lineWidth = 10;
    ctx.beginPath();
    ctx.arc(cx, cy, d / 2 - 5, 0, Math.PI * 2);
    ctx.stroke();
  }

  // Home-stretch chip (final day): scarlet pill above the headline
  let headTop = F.head;
  if (homeStretch) {
    const text = "Home stretch";
    ctx.font = "700 30px 'Manrope', sans-serif";
    const tw = ctx.measureText(text).width;
    const h = 58;
    ctx.fillStyle = C.scarlet;
    roundRect(ctx, P, headTop - 40, tw + 56, h, h / 2);
    ctx.fill();
    ctx.fillStyle = C.sand;
    ctx.textBaseline = "middle";
    ctx.fillText(text, P + 28, headTop - 40 + h / 2 + 1);
    ctx.textBaseline = "alphabetic";
    headTop += 60;
  }

  // Headline: serif, ink, left-aligned, up to three lines
  ctx.fillStyle = C.ink;
  ctx.textAlign = "left";
  let size = F.headSize;
  ctx.font = `500 ${size}px 'Cormorant Garamond', serif`;
  let lines = wrap(ctx, headline, F.headMax);
  if (lines.length > 2) {
    size = Math.round(F.headSize * 0.84);
    ctx.font = `500 ${size}px 'Cormorant Garamond', serif`;
    lines = wrap(ctx, headline, F.headMax + 50);
  }
  const lh = size * 1.02;
  let y = headTop + size * 0.8;
  lines.forEach((l) => { ctx.fillText(l, P, y); y += lh; });

  // Amount as the hero, with the support line beside it; shrinks to fit the row
  if (amountText) {
    const ay = Math.max(y + 150, format === "story" ? 1300 : 800);
    ctx.font = "600 36px 'Manrope', sans-serif";
    const sw = Math.max(...support.map((s) => ctx.measureText(s).width));
    let asize = F.amount;
    ctx.font = `500 ${asize}px 'Cormorant Garamond', serif`;
    while (asize > 120 && ctx.measureText(amountText).width + 34 + sw > W - P * 2) {
      asize -= 10;
      ctx.font = `500 ${asize}px 'Cormorant Garamond', serif`;
    }
    ctx.fillStyle = C.ink;
    ctx.fillText(amountText, P - 6, ay);
    const aw = ctx.measureText(amountText).width;
    ctx.font = "600 36px 'Manrope', sans-serif";
    ctx.fillStyle = "rgba(26,23,20,0.8)";
    support.forEach((s, i) => ctx.fillText(s, P + aw + 34, ay - 46 + i * 46));
  } else {
    ctx.font = "600 40px 'Manrope', sans-serif";
    ctx.fillStyle = "rgba(26,23,20,0.8)";
    support.forEach((s, i) => ctx.fillText(s, P, y + 30 + i * 52));
  }

  // Signature in the donor's hand
  if (signature) {
    ctx.save();
    ctx.translate(P, F.bottom - 6);
    ctx.rotate((-2 * Math.PI) / 180);
    ctx.fillStyle = C.ink;
    ctx.font = "500 72px 'Caveat', cursive";
    ctx.fillText(signature, 0, 0);
    ctx.restore();
  }

  // Sign-off: five palette dashes + URL, right-aligned
  const dw = 52, dh = 15, gap = 12;
  let dx = W - P - (dw * 5 + gap * 4);
  DASHES.forEach((c) => { ctx.fillStyle = c; roundRect(ctx, dx, F.bottom - 70, dw, dh, dh / 2); ctx.fill(); dx += dw + gap; });
  ctx.fillStyle = C.ink;
  ctx.font = "600 34px 'Manrope', sans-serif";
  ctx.textAlign = "right";
  ctx.fillText("myrichaunt.org", W - P, F.bottom - 6);

  return canvas.toDataURL("image/png");
};

const dataUrlToBlob = (dataUrl) => {
  const [meta, b64] = dataUrl.split(",");
  const mime = /data:(.*?);/.exec(meta)[1];
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type: mime });
};

export const ImpactCard = ({ hasCampaign, studentName, campaignImage, campaignGoal, campaignRaisedAfter, campaignDaysLeft, amount, showName, displayName }) => {
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [format, setFormat] = useState("square");
  const [includeAmount, setIncludeAmount] = useState(false); // amounts stay private unless the donor opts in
  const [art, setArt] = useState(null); // { dataUrl, blob }

  const shareUrl = typeof window !== "undefined" && window.location ? window.location.href : "https://myrichaunt.org";
  const hasProgress = hasCampaign && typeof campaignGoal === "number" && campaignGoal > 0 && typeof campaignRaisedAfter === "number";
  const progressPct = hasProgress ? Math.max(0, Math.min(100, Math.round((campaignRaisedAfter / campaignGoal) * 100))) : null;
  const homeStretch = hasCampaign && typeof campaignDaysLeft === "number" && campaignDaysLeft <= 1;

  const headline = hasCampaign ? `I supported ${studentName} using ${ORG_NAME}.` : `I supported students using ${ORG_NAME}.`;
  const support = hasCampaign
    ? hasProgress ? [`toward ${studentName}'s ${money(campaignGoal)} goal`, `${progressPct}% funded`] : [`toward ${studentName}'s campaign`]
    : ["to the General Fund,", "where it's needed most"];
  const signature = showName ? (displayName || "").trim() : "";
  const amountText = includeAmount ? money(amount) : "";

  const caption = hasCampaign
    ? homeStretch
      ? `I supported ${studentName} using ${ORG_NAME} in the home stretch. Final hours to close the gap. Chip in: ${shareUrl}`
      : `I supported ${studentName} using ${ORG_NAME}. Tax-deductible giving through a 501(c)(3) that awards grants to verified students. Chip in: ${shareUrl}`
    : `I supported students using ${ORG_NAME}. Tax-deductible giving through a 501(c)(3) that awards grants to verified students. ${shareUrl}`;

  const key = [format, headline, amountText, support.join("|"), signature, homeStretch, campaignImage || ""].join("§");
  useEffect(() => {
    let cancelled = false;
    setArt(null);
    (async () => {
      const [logo, photo] = await Promise.all([loadImage(IMAGES.logoColor), loadImage(hasCampaign ? campaignImage : null)]);
      const dataUrl = await drawCard({ format, headline, amountText, support, signature, homeStretch, logo, photo });
      if (!cancelled) setArt({ dataUrl, blob: dataUrlToBlob(dataUrl) });
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const download = (dataUrl) => {
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = `my-rich-aunt-gift-${format}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const onDownload = () => {
    if (!art) return;
    download(art.dataUrl);
    toast.success("Share card downloaded", { description: "Post it anywhere to help the goal along." });
  };

  const onShare = async () => {
    if (!art) return;
    setBusy(true);
    try {
      const file = new File([art.blob], `my-rich-aunt-gift-${format}.png`, { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try { await navigator.share({ files: [file], text: caption, title: ORG_NAME }); return; } catch (e) { /* next */ }
      }
      if (navigator.share) {
        try { await navigator.share({ text: caption, url: shareUrl, title: ORG_NAME }); return; } catch (e) { /* next */ }
      }
      try { await navigator.clipboard.writeText(caption); } catch (e) { /* noop */ }
      download(art.dataUrl);
      toast.success("Caption copied + image downloaded", { description: "Paste the caption with your post." });
    } catch (e) {
      toast.error("Couldn't share the card", { description: "Try downloading instead." });
    } finally {
      setBusy(false);
    }
  };

  const onCopyCaption = async () => {
    try {
      await navigator.clipboard.writeText(caption);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
      toast.success("Caption copied");
    } catch (e) {
      toast.error("Couldn't copy. Try selecting the text instead.");
    }
  };

  return (
    <div data-testid="impact-card" className="mt-6 overflow-hidden rounded-lg border border-border bg-bone p-1.5">
      <div className="mb-2 flex items-center justify-between gap-3 px-1 pt-1">
        <div className="inline-flex rounded-full bg-ink/5 p-0.5" role="tablist" aria-label="Card size" data-testid="impact-format-toggle">
          {[["square", "Square"], ["story", "Story"]].map(([k, l]) => (
            <button key={k} type="button" role="tab" aria-selected={format === k} onClick={() => setFormat(k)} data-testid={`impact-format-${k}`} className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${format === k ? "bg-ink text-sand" : "text-ink/70 hover:text-ink"}`}>
              {l}
            </button>
          ))}
        </div>
        <label className="flex cursor-pointer items-center gap-2 text-xs text-ink">
          <span>Include my gift amount</span>
          <button type="button" role="switch" aria-checked={includeAmount} aria-label="Include my gift amount" data-testid="impact-include-amount" onClick={() => setIncludeAmount((v) => !v)} className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors ${includeAmount ? "bg-tangerine" : "bg-ink/20"}`}>
            <span className={`inline-block h-4 w-4 rounded-full bg-bone shadow transition-transform ${includeAmount ? "translate-x-[18px]" : "translate-x-0.5"}`} />
          </button>
        </label>
      </div>
      <div data-testid="impact-card-preview" className={`relative mx-auto w-full overflow-hidden rounded-md bg-yellow ${format === "story" ? "aspect-[9/16] max-w-[62%]" : "aspect-square"}`} data-ready={Boolean(art)} data-format={format}>
        {art && <img src={art.dataUrl} alt={headline} className="h-full w-full" data-testid="impact-card-image" />}
      </div>

      <div className="mt-3 grid grid-cols-3 gap-2 px-1 pb-1">
        <motion.button data-testid="impact-share" onClick={onShare} disabled={busy || !art} whileTap={{ scale: 0.96 }} className="inline-flex items-center justify-center gap-1.5 rounded-full bg-ink px-3 py-2.5 text-xs font-semibold text-sand transition-colors hover:bg-ink/90 disabled:opacity-60">
          <Share2 className="h-3.5 w-3.5" strokeWidth={2} /> Share
        </motion.button>
        <motion.button data-testid="impact-download" onClick={onDownload} disabled={!art} whileTap={{ scale: 0.96 }} className="inline-flex items-center justify-center gap-1.5 rounded-full border border-ink/20 bg-bone px-3 py-2.5 text-xs font-semibold text-ink transition-colors hover:border-ink disabled:opacity-60">
          <Download className="h-3.5 w-3.5" strokeWidth={2} /> Download
        </motion.button>
        <motion.button data-testid="impact-copy-caption" onClick={onCopyCaption} whileTap={{ scale: 0.96 }} className="inline-flex items-center justify-center gap-1.5 rounded-full border border-ink/20 bg-bone px-3 py-2.5 text-xs font-semibold text-ink transition-colors hover:border-ink">
          {copied ? <Check className="h-3.5 w-3.5 text-grass" strokeWidth={2} /> : <Copy className="h-3.5 w-3.5" strokeWidth={2} />}
          {copied ? "Copied" : "Caption"}
        </motion.button>
      </div>
      <p className="px-1 pb-2 pt-1 text-center text-[13px] text-olive">
        <Send className="mr-1 inline h-3 w-3 text-tangerine" strokeWidth={2} />
        Post it to boost {hasCampaign ? `${studentName}'s` : "the"} reach through your network.
      </p>
    </div>
  );
};
